import React, { useEffect, useMemo, useState, useRef } from 'react';
import { __, sprintf } from '@wordpress/i18n';
import { isEmpty, isNil } from 'ramda';

// store
import { storeSet, useStoreValue } from '../../store';

// api
import AssignedCompanyService from '../../service/assigned-company-service';

// components
import { Button } from 'primereact/button';
import { useNavigate, useParams } from 'react-router-dom';
import DocumentsToEvaluateTable from './components/DocumentsToEvaluateTable';
import { Skeleton } from 'primereact/skeleton';
import CompanyInfo from './components/CompanyInfo';
import CompanyService from '../../service/company-service';
import CompanyDocumentsService from '../../service/company-documents-service';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';


const DocumentsEvaluatePreInstructor = () => {
    const isAsyncRequest = useStoreValue('isAsyncRequest');
    const userData = useStoreValue('userData');
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState({});
    const toastSolleciti = useRef(null);
    const [solleciti, setSolleciti] = useState({ visibile: false, invio: false });

    const inviaSolleciti = () => {
        setSolleciti(s => ({ ...s, invio: true }));
        CompanyDocumentsService.runExpirationReminders(
            (resp) => {
                setSolleciti({ visibile: false, invio: false });
                if (toastSolleciti.current) {
                    toastSolleciti.current.show({ severity: resp.status === 'SUCCESS' ? 'success' : 'warn', summary: '', detail: __('Solleciti di scadenza inviati ai fornitori con documenti in scadenza.', 'htr') });
                }
            },
            (resp) => {
                setSolleciti({ visibile: false, invio: false });
                if (toastSolleciti.current) {
                    toastSolleciti.current.show({ severity: 'error', summary: '', detail: (resp && resp.message) || __('Invio non riuscito.', 'htr') });
                }
            }
        );
    };
    const shouldBeReadOnly = useMemo(() => {
        return isNil(data.assignedToUserId)
            ? null
            : data.assignedToUserId !== userData.id || ['REJECTED', 'APPROVED'].includes(data.companyStatus)
    }, [userData, data]);

    const goToEvaluationsPage = () => {
        navigate('/fornitori');
    }

    const getCompanyCallback = (resp) => {
        if (resp.status === 'SUCCESS') {
            setData(resp.data);
        }
        storeSet('unsetAsyncRequest');
    }

    const errGetCompanyCallback = (resp) => {
        storeSet('unsetAsyncRequest');
    }

    const handleDownloadAllDocs = () => {
        storeSet('setAsyncRequest');
        CompanyService.getCompanyDocsZip(id, getDocsCallback, errGetDocsCallback);
    }

    const getDocsCallback = (resp) => {
        try {
            const file = new Blob([resp], { type: 'application/zip' })
            const url = window.URL.createObjectURL(file);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `documents-${id}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            storeSet('unsetAsyncRequest');
        } catch (err) {
            console.log(err);
            storeSet('unsetAsyncRequest');
        }
    }

    const errGetDocsCallback = (resp) => {
        storeSet('unsetAsyncRequest');
    }

    useEffect(() => {
        storeSet('setAsyncRequest');

        if (id && !isEmpty(id)) {
            AssignedCompanyService.getAssignedCompanyByCompanyId(id, getCompanyCallback, errGetCompanyCallback)
        }
    }, [id]);

    return (
        <div className="appPage">
            <Toast ref={toastSolleciti}/>
            <Dialog
                visible={solleciti.visibile}
                modal
                header={__('Invia solleciti scadenze', 'htr')}
                style={{ maxWidth: '520px', width: '100%' }}
                onHide={() => setSolleciti({ visibile: false, invio: false })}
                footer={<div>
                    <Button type="button" outlined label={__('Annulla', 'htr')} onClick={() => setSolleciti({ visibile: false, invio: false })}/>
                    <Button type="button" disabled={solleciti.invio} label={__('Invia', 'htr')} onClick={inviaSolleciti}/>
                </div>}>
                <p>{__('Verranno inviati gli avvisi di scadenza (portale + email) a tutti i fornitori con documenti in scadenza nei prossimi giorni. Procedere?', 'htr')}</p>
            </Dialog>
            <div className="appPage__pageHeader">
                <h1>{sprintf(__('Fornitore: %s', 'htr'), data.companyName)}</h1>
            </div>

            <div className="appPage__spacer"></div>

            <div className="appPageSection__row">
                <Button
                    type="button"
                    outlined
                    onClick={goToEvaluationsPage}
                    label={__('Indietro', 'htr')}
                    icon="pi pi-arrow-left" iconPos="left"/>
                <Button
                    type="button"
                    severity="warning"
                    onClick={() => setSolleciti(s => ({ ...s, visibile: true }))}
                    label={__('Invia solleciti scadenze', 'htr')}
                    icon="pi pi-bell" iconPos="right"/>
            </div>

            <div className="appPage__spacer"></div>

            {!isAsyncRequest && !isEmpty(data)
                ? <div className="appPage__content">
                    <CompanyInfo data={data}/>

                    {id && !isNil(shouldBeReadOnly)
                        ? <div>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 20
                            }}>
                                <h2 style={{ margin: 0 }}>{__('Documenti dell\'Azienda', 'htr')}</h2>
                                <Button
                                    type="button"
                                    onClick={handleDownloadAllDocs}
                                    label={__('Scarica tutti (.zip)', 'htr')}
                                    icon="pi pi-download" iconPos="right"/>
                            </div>
                            <DocumentsToEvaluateTable
                                companyId={id}
                                readOnly={shouldBeReadOnly}
                                applicationId={data.applicationId}
                                companyStatus={data.companyStatus}/>
                        </div>
                        : null}
                </div>
                : <>
                    <Skeleton width="20%" height="1rem" className="mb-2"></Skeleton>
                    <Skeleton width="100%" height="2rem" className="mb-8"></Skeleton>
                    <Skeleton width="20%" height="1rem" className="mb-2"></Skeleton>
                    <Skeleton width="100%" height="4rem" className="mb-8"></Skeleton>
                    <Skeleton width="20%" height="1rem" className="mb-2"></Skeleton>
                    <Skeleton width="100%" height="2rem" className="mb-8"></Skeleton>
                    <Skeleton width="20%" height="1rem" className="mb-2"></Skeleton>
                    <Skeleton width="100%" height="4rem"></Skeleton>
                </>}
        </div>
    )
}

export default DocumentsEvaluatePreInstructor;